import { useFeatureFlag } from '@artic/ui/feature-flags';
import type { QueryClient } from '@tanstack/react-query';
import type { AnyRouteMatch } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { EditProjectButton } from './edit-project-button';
import { ProjectStateChip } from './project-state-chip';
import { ProjectViewToggle } from './project-view-toggle';
import { projectPlanningDetailQueryOptions } from './queries';
import type { ProjectPlanningDetail } from './types';

/**
 * ENG-720 Band 1. The Project view's top row is the application header itself:
 * `Projects > Apple Park  ● Released  Update Project` on the left, the
 * `Grid | Timeline` toggle on the right. Both halves are declared per-route
 * through `staticData` (see `router-types.d.ts`) rather than rendered by the
 * page, which is what retired the old `ProjectPageHeader` bar.
 *
 * Every `projects/$projectId*` route is a flat, non-nested sibling, so each one
 * declares these itself. Keeping the two resolvers here means that's a one-line
 * reference per route with nothing to drift.
 *
 * Both read the project detail straight out of the query cache — the same
 * source the `breadcrumb` resolver uses — so they add no fetch. `null` while
 * the detail is still in flight; `RouteHeaderActions` / `RouteBreadcrumbs`
 * re-resolve on every query-cache event, so they fill in as soon as it lands.
 */

function readProject(match: AnyRouteMatch, queryClient: QueryClient) {
  const { projectId } = match.params as { projectId: string };
  return {
    projectId,
    project: queryClient.getQueryData(projectPlanningDetailQueryOptions(projectId).queryKey),
  };
}

/**
 * Both resolvers render through a component so they can consult the `planner`
 * flag. They must: the route `loader` prefetches the project detail
 * unconditionally, so with the flag off the cache is warm and this chrome would
 * still render — above the page's own "Feature Not Available" panel. The
 * retired `ProjectPageHeader` got this for free by living inside the gated
 * subtree; declaring Band 1 on the route does not.
 */
function PlannerOnly({ children }: { children: ReactNode }) {
  const plannerEnabled = useFeatureFlag('planner');
  if (!plannerEnabled) return null;
  return <>{children}</>;
}

function ProjectBreadcrumbBadges({ project }: { project: ProjectPlanningDetail }) {
  return (
    <PlannerOnly>
      <ProjectStateChip projectStatus={project.project_status} />
      <EditProjectButton project={project} />
    </PlannerOnly>
  );
}

function ProjectHeaderActions({ projectId }: { projectId: string }) {
  return (
    <PlannerOnly>
      <ProjectViewToggle projectId={projectId} />
    </PlannerOnly>
  );
}

/** State chip + Update Project, rendered after the final breadcrumb. */
export function projectBreadcrumbBadges(match: AnyRouteMatch, queryClient: QueryClient): ReactNode {
  const { project } = readProject(match, queryClient);
  if (!project) return null;
  return <ProjectBreadcrumbBadges project={project} />;
}

/**
 * `Grid | Timeline`, pinned to the trailing edge of the header. Declared only
 * by the two routes the toggle actually switches between — the release,
 * redraft, run-sheets and legacy gantt routes deliberately omit it, exactly as
 * they previously passed `showViewToggle={false}`.
 */
export function projectHeaderActions(match: AnyRouteMatch): ReactNode {
  const { projectId } = match.params as { projectId: string };
  if (!projectId) return null;
  return <ProjectHeaderActions projectId={projectId} />;
}
